import api from './api.js';
import { axiosInstance } from '../lib/axios.js';

const throwUnsupportedLifecycleHelper = (helperName) => {
  throw new Error(`${helperName} is no longer supported by the frontend match service.`);
};

/**
 * Service for match-related API operations
 */
const MatchService = {
  /**
   * Get matches with pagination and filters
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number
   * @param {number} params.limit - Items per page
   * @param {string} params.teamId - Filter by team ID
   * @param {string} params.status - Filter by match status
   * @param {string} params.sort - Sort field and direction (e.g. '-date' for descending date)
   * @param {number} retries - Number of retries for network errors
   * @returns {Promise<Object>} - Matches with pagination info
   */
  async getMatches(params = {}, retries = 2) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.teamId) queryParams.append('teamId', params.teamId);
      if (params.leagueId) queryParams.append('leagueId', params.leagueId);
      if (params.status) queryParams.append('status', params.status);
      if (params.sort) queryParams.append('sort', params.sort);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/matches?${queryString}` : '/matches';
      
      return await api.get(url);
    } catch (error) {
      console.error('Error fetching matches:', error);
      
      // Retry logic for network errors
      if (retries > 0 && (error.code === 'ERR_NETWORK' || !error.response)) {
        console.log(`Network error in getMatches, retrying... (${retries} attempts left)`);
        // Wait before retrying with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, 2 - retries), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
        return MatchService.getMatches(params, retries - 1);
      }
      
      throw error;
    }
  },

  /**
   * Get match details by ID
   * @param {string} matchId - Match ID
   * @param {number} retries - Number of retries for network errors
   * @returns {Promise<Object>} - Match details
   */
  async getMatchById(matchId, retries = 2) {
    try {
      return await api.get(`/matches/${matchId}`);
    } catch (error) {
      console.error(`Error fetching match ${matchId}:`, error);
      
      // Retry logic for network errors
      if (retries > 0 && (error.code === 'ERR_NETWORK' || !error.response)) {
        console.log(`Network error in getMatchById, retrying... (${retries} attempts left)`);
        // Wait before retrying with exponential backoff
        const delay = Math.min(1000 * Math.pow(2, 2 - retries), 5000);
        await new Promise(resolve => setTimeout(resolve, delay));
        return MatchService.getMatchById(matchId, retries - 1);
      }
      
      throw error;
    }
  },

  async updateMatchStatus() {
    throwUnsupportedLifecycleHelper('updateMatchStatus');
  },

  async simulateMatch() {
    throwUnsupportedLifecycleHelper('simulateMatch');
  },

  /**
   * Get match summary data including commentary and player statistics
   * @param {string} matchId - Match ID
   * @returns {Promise<Object>} - Match summary data
   */
  async getMatchPlayerStats(matchId) {
    if (!matchId) throw new Error('matchId required');
    try {
      // Accept 200 (stats ready) and 404 (stats not available) as "successful" responses
      const response = await axiosInstance.get(`/matches/${matchId}/player-stats`, {
        validateStatus: (status) => status === 200 || status === 404,
      });
      if (response.status === 404) {
        // Stats not generated yet – return null so caller can fallback to summary playerStats
        return null;
      }
      return response.data;
    } catch (error) {
      console.error(`[MatchService] Error fetching player stats for match ${matchId}:`, error);
      throw error;
    }
  },

  async getMatchSummary(matchId) {
    try {
      console.log(`[MatchService] Fetching summary for match ${matchId}`);

      // Use axiosInstance directly so we can customize validateStatus. We treat 400 (no summary yet) as a
      // non-error response to prevent noisy console errors from Axios/React.
      const response = await axiosInstance.get(`/matches/${matchId}/summary`, {
        // Accept both 200 (summary available) and 400 (summary not ready) as "successful".
        validateStatus: (status) => status === 200 || status === 400,
      });

      // If backend returns 400 it means summary is not available yet – return null so caller can handle gracefully
      if (response.status === 400) {
        return null;
      }
      // Otherwise return the actual data
      return response.data;
    } catch (error) {
      console.error(`[MatchService] Error fetching summary for match ${matchId}:`, error);
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        throw new Error('Match not found');
      } else {
        throw new Error('Failed to load match summary. Please try again.');
      }
    }
  }
};

export default MatchService;
